import { lazy, Suspense, ReactElement, ReactNode, ReactPortal } from 'react';

// Computed at module load — prevents new Date() from being called during render
const CURRENT_YEAR = new Date().getFullYear();
import {
  InstagramIcon,
  LinkedInIcon,
  YouTubeIcon,
} from '../icons/SocialIcons.js';

// Lazy-load slider so framer-motion is NOT pulled into the initial bundle
const InfiniteHorizontalSlider = lazy(() => import('../UI/InfiniteHorizontalSlider.tsx'));
import { getPartners } from '../../../runtime-db/teamData/partnersData.tsx';
import useDeviceSize from '../../hooks/useDeviceSize.tsx';
import { cn } from '../../../utils.ts';
import { EMPTY_STRING } from '../../../shared/constants/empty-string.constant.ts';
import Newsletter from './newsletter/Newsletter.tsx';
import GoogleTranslateWidget from '../internationalization/GoogleTranslateWidget';

const getNegativeImageUrls = () => {
  const partners = getPartners();
  return partners.map(partner => partner.negativeImage);
};

const negativeImageUrls = getNegativeImageUrls();

export const LinkWithSocialIcon = (props: {
  url: string;
  children:
      | string
      | number
      | boolean
      | ReactElement
      | Iterable<ReactNode>
      | ReactPortal;
}) => {
  return (
      <a
          href={props.url}
          target='_blank'
          rel="noopener noreferrer"
          type='button'
          className='m-1 leading-normal text-white uppercase transition duration-150 ease-in-out border-2 border-white rounded-full h-9 w-9 hover:bg-black hover:bg-opacity-5 focus:outline-1 focus:ring-2 focus:outline-ghColorPrimary-400'
          data-te-ripple-init=''
          data-te-ripple-color='light'
          aria-label='link to Reboot Facebook'
      >
        {props.children}
      </a>
  );
};

const Footer = () => {
  const [viewportWidth, viewportHeight] = useDeviceSize();
  const sliderHeight = viewportWidth >= viewportHeight ? "h-[12vh]" : "h-[9vh]";

  return (
      <div className='mt-52'>
        <footer className='flex flex-col items-center text-center text-white bg-neutral-900'>
          <div className='container w-full max-w-full px-6 pt-6'>
            <div className='flex justify-center mb-6'>
              <LinkWithSocialIcon url='https://www.instagram.com/gamehearts.eu/'>
                <InstagramIcon twClasses={'mx-auto h-full w-4'} />
              </LinkWithSocialIcon>
              <LinkWithSocialIcon url='https://uk.linkedin.com/company/gamehearts-research'>
                <LinkedInIcon twClasses={'mx-auto h-full w-4'} />
              </LinkWithSocialIcon>
              <LinkWithSocialIcon url='https://www.youtube.com/@GAMEHEARTSresearch'>
                <YouTubeIcon twClasses={'mx-auto h-full w-4'} />
              </LinkWithSocialIcon>
            </div>
            <Newsletter />

            <div className='mb-6'>
              <GoogleTranslateWidget />
            </div>
            <div className='mb-6'>
              <p>
                  Funded by the European Union. Views and opinions expressed are
                  however those of the author(s) only and do not necessarily reflect
                  those of the European Union or European Research Executive Agency.
                  Neither the European Union nor the European Research Executive
                  Agency can be held responsible for them.
              </p>
            </div>
            <div className='grid mt-24 md:grid-cols-2'>
              <div className='mb-6 space-y-1'>
                <div className='min-w-[150px] max-w-[300px]'>
                  <img
                      className='w-full'
                      src={'/logos/003.EN_FundedbytheEU_RGB_WHITE Outline.png'}
                      alt='Funded by the European Union'
                      loading="lazy"
                  />
                </div>

              </div>

              <div className='mb-6'>
                <a
                    href='https://sarikakis.com/'
                    className='text-white mb-2.5 font-bold uppercase'
                >
                  Gamehearts media governance and industries research lab
                </a>

                <ul className='mb-0 list-none'>
                  <li>
                    <a
                        href={EMPTY_STRING}
                        className='text-white'
                    >
                      Department of Communication
                    </a>
                  </li>
                  <li>
                    <a
                        href={EMPTY_STRING}
                        className='text-white'
                    >
                      University of Vienna
                    </a>
                  </li>
                  <li>
                    <a
                        href={EMPTY_STRING}
                        className='text-white'
                    >
                      Waehringerstr 29
                    </a>
                  </li>
                  <li>
                    <a
                        href={EMPTY_STRING}
                        className='text-white'
                    >
                      A-1090 Vienna
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className={cn('w-full max-w-full overflow-hidden py-2', sliderHeight)} >
              <Suspense fallback={<div className={cn('w-full', sliderHeight)} />}>
                <InfiniteHorizontalSlider duration={13}>
                  {negativeImageUrls.map((item, index) => (
                      <img
                          // Fixed slot width + h-full keeps every logo inside the strip (no vertical
                          // margin, which used to push logos past the overflow-hidden edge) and gives
                          // the slider a width it can measure before the lazy images have loaded.
                          className='mx-3 h-full w-32 sm:w-40 object-contain'
                          src={item}
                          key={index}
                          alt="logo"
                          loading="lazy"
                      />
                  ))}
                </InfiniteHorizontalSlider>
              </Suspense>
            </div>
          </div>
          <div
              className='p-4 text-center
                     bg-rgba(0, 0, 0, 0.2) mt-8 mb-0'
          >
            © {CURRENT_YEAR} CodeSocietyHub OÜ. All rights reserved.
          </div>
        </footer >
      </div >
  );
};

export default Footer;
